import {createSearchParams, useParams} from 'react-router-dom'
import {getRequest} from '../../../services/base'
import {useContext, useEffect, useMemo, useRef, useState} from 'react'
import {NavContext} from '../../../context/global_context'
import {GraphMissingImagesContext, SET_GRAPH_MISSING_IMAGES_LIST,} from './context/context'
import InputText from '../../common/InputText'
import GraphMissingImagesTable from './GraphMissingImagesTable'
import DtcValidationButton from '../../common/DtcValidationButton'
import {getSessionStorageData} from '../../../utils/SessionStorage'
import {downloadFileBlob, toTitleCase} from '../../../utils/common'
import {Flex, Form, Row, Select, Space, Tooltip} from 'antd'
import {ExportOutlined} from '@ant-design/icons'
import useDebouncedValue from '../../../utils/common_function'
import {useIntl} from 'react-intl'
import { notifyError } from '../../../utils/notifications'

const GraphMissingImagesList = () => {
  const intl = useIntl()
  const { vehicle } = useParams()

  const [state, dispatch] = useContext(GraphMissingImagesContext)
  const [, navDispatch] = useContext(NavContext)

  const [search, setSearch] = useState('')
  const [pagination, setPagination] = useState({})
  const [versionOptions, setVersionOptions] = useState([])
  const [selectedVersion, setSelectedVersion] = useState(null)
  const [categoryOptions, setCategoryOptions] = useState([])
  const [selectedCategory, setSelectedCategory] = useState([])
  const [vinOptions, setVinOptions] = useState([])
  const [selectedVin, setSelectedVin] = useState([])
  const [isMissingConnectorOrTerminalImg, setIsMissingConnectorOrTerminalImg] =
    useState(false)
  const [isMissingVehicleImg, setIsMissingVehicleImg] = useState(false)
  const currentUrl = useRef(`/api/graph_missing_images/?vehicle=${vehicle}`)

  const debouncedSearch = useDebouncedValue(search, 500)

  const vehicle_redirect_url = getSessionStorageData('vehicle')?.search
    ? `/vehicles/?${createSearchParams(getSessionStorageData('vehicle'))}`
    : `/vehicles`

  useEffect(() => {
    navDispatch({
      type: 'header',
      data: {
        header: intl.formatMessage({ id: 'layout.adminTools' }),
        sub_header:
          intl.formatMessage({ id: 'layout.vehicleList' }) +
          ` / ${vehicle} / ` +
          intl.formatMessage({ id: 'sidebar.graphMissingImages' }),
        id: vehicle,
        show_back_btn: true,
        dtc_details: [],
        back_btn_url: vehicle_redirect_url,
        full_width_container: false,
      },
    })
    getGraphMissingImagesVersions()
    getGraphMissingImagesCategories()
    getGraphMissingImagesVins()
  }, [])

  useEffect(() => {
    currentUrl.current = buildUrl({
      vehicle,
      search,
      data_version: selectedVersion || '',
      is_missing_connector_or_terminal_img: isMissingConnectorOrTerminalImg
        ? 1
        : 0,
      is_missing_vehicle_img: isMissingVehicleImg ? 1 : 0,
      category: selectedCategory,
      vin: selectedVin,
    })
    getGraphMissingImagesList()
  }, [
    debouncedSearch,
    selectedVersion,
    selectedCategory,
    selectedVin,
    isMissingConnectorOrTerminalImg,
    isMissingVehicleImg,
  ])

  const buildUrl = ({
    vehicle,
    search,
    data_version,
    is_missing_connector_or_terminal_img,
    is_missing_vehicle_img,
    category,
    vin,
    limit,
    offset,
  }) => {
    const params = new URLSearchParams({
      vehicle,
      search,
      data_version,
      is_missing_connector_or_terminal_img,
      is_missing_vehicle_img,
    })
    if (category?.length) params.set('category', category.join(','))
    if (vin?.length) params.set('vin', vin.join(','))
    if (limit) params.set('limit', limit)
    if (offset) params.set('offset', offset)
    return `/api/graph_missing_images/?${params.toString()}`
  }

  const getGraphMissingImagesList = async (url) => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({
          id: 'graphMissingImages.fetchingData',
        }),
        showLoader: true,
      },
      notification: {},
    })

    currentUrl.current = url ? url : currentUrl.current
    let res = await getRequest(currentUrl.current)
    setPagination(res.pagination)
    dispatch({ type: SET_GRAPH_MISSING_IMAGES_LIST, data: res.results })

    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })

    // After the loader dispatch above: a 'loader' action overwrites `notification`,
    // so notifying before it would be wiped out in the same React batch.
    if (res?.status === false) {
      notifyError(navDispatch, intl.formatMessage({ id: 'graphMissingImages.loadError' }))
    }
  }

  const getGraphMissingImagesVersions = async () => {
    let res = await getRequest(
      `/api/graph_missing_images/versions/?vehicle=${vehicle}`
    )
    setVersionOptions(
      (res.data || []).map((val) => ({ value: val, label: val }))
    )
  }

  const getGraphMissingImagesCategories = async () => {
    let res = await getRequest(
      `/api/graph_missing_images/category/?vehicle=${vehicle}`
    )
    setCategoryOptions(
      (res.data || []).map((val) => ({ value: val, label: toTitleCase(val) }))
    )
  }

  const getGraphMissingImagesVins = async () => {
    let res = await getRequest(
      `/api/graph_missing_images/vin/?vehicle=${vehicle}`
    )
    setVinOptions((res.data || []).map((val) => ({ value: val, label: val })))
  }

  const exportGraphMissingImages = async () => {
    navDispatch({
      type: 'loader',
      data: {
        caption: intl.formatMessage({ id: 'graphMissingImages.exporting' }),
        showLoader: true,
      },
      notification: {},
    })

    let res = await getRequest(
      `/api/graph_missing_images/export/?vehicle=${vehicle}&search=${search}&data_version=${selectedVersion || ''}&is_missing_vehicle_img=${isMissingVehicleImg ? 1 : 0}&is_missing_connector_or_terminal_img=${isMissingConnectorOrTerminalImg ? 1 : 0}&category=${formatFilterUrl(selectedCategory)}&vin=${formatFilterUrl(selectedVin)}`,
      true,
      true
    )
    navDispatch({
      type: 'loader',
      data: {
        showLoader: false,
      },
      notification: {},
    })
    // `res?.msg` is redundant: base.js sets status false on every failure,
    // and an error body is never a Blob.
    if (res?.status === false || !(res instanceof Blob)) {
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'error',
          msg: intl.formatMessage({ id: 'graphMissingImages.exportError' }),
        },
      })
    } else {
      downloadFileBlob(res, `missing_images-${vehicle}.csv`)
      navDispatch({
        type: 'notification',
        data: {
          notification_type: 'success',
          msg: intl.formatMessage({
            id: 'graphMissingImages.exportSuccess',
          }),
        },
      })
    }
  }

  const formatFilterUrl = (values) => {
    if (values?.length) {
      return values.join(',')
    }
    return ''
  }

  const dataSource = useMemo(() => {
    if (!state?.list?.length) return []

    return state?.list?.map((item, index) => ({
      ...item,
      key: `graph-missing-images-${index}`,
      rowKey: `graph-missing-images-${item.id}`,
      index: index,
    }))
  }, [state.list])

  return (
    <div className="page sub-page">
      <Row className="body">
        <Form className="full-width">
          <Flex
            gap={16}
            wrap={false}
            className="full-width"
            align="center"
          >
            <InputText
              name="search_graph_missing_images"
              type="search"
              placeholder={intl.formatMessage({
                id: 'graphMissingImages.searchPlaceholder',
              })}
              value={search}
              isRequired={false}
              wrapperClass="search-graph-missing-images"
              onChange={(value) => {
                setSearch(value)
              }}
            />

            <Select
              placeholder={intl.formatMessage({
                id: 'graphMissingImages.selectVersion',
              })}
              options={versionOptions}
              value={selectedVersion}
              onChange={(value) => setSelectedVersion(value)}
              className="graph-missing-images-select-version"
              allowClear
              showSearch
            />

            <Select
              mode="multiple"
              placeholder={intl.formatMessage({
                id: 'graphMissingImages.selectCategory',
              })}
              options={categoryOptions}
              value={selectedCategory}
              onChange={(values) => setSelectedCategory(values)}
              className="graph-missing-images-select-category"
              allowClear
              showSearch
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => (
                <Tooltip
                  title={omittedValues.map((v) => v.label).join(', ')}
                >
                  <span>+{omittedValues.length}</span>
                </Tooltip>
              )}
            />

            <Select
              mode="multiple"
              placeholder={intl.formatMessage({
                id: 'graphMissingImages.selectVin',
              })}
              options={vinOptions}
              value={selectedVin}
              onChange={(values) => setSelectedVin(values)}
              className="graph-missing-images-select-vin"
              allowClear
              showSearch
              maxTagCount="responsive"
              maxTagPlaceholder={(omittedValues) => (
                <Tooltip
                  title={omittedValues.map((v) => v.label).join(', ')}
                >
                  <span>+{omittedValues.length}</span>
                </Tooltip>
              )}
            />

            <Space
              size="middle"
              align="center"
              style={{ marginLeft: 'auto' }}
            >
              <DtcValidationButton
                actionsLabel={intl.formatMessage({
                  id: 'graphMissingImages.actions',
                })}
                extraMenuItems={[
                  {
                    key: 'export',
                    icon: <ExportOutlined />,
                    label: intl.formatMessage({
                      id: 'graphMissingImages.export',
                    }),
                    onClick: exportGraphMissingImages,
                  },
                ]}
              />
            </Space>
          </Flex>
        </Form>

        <Flex vertical gap={16} style={{ width: '100%' }}>
          <GraphMissingImagesTable
            dataSource={dataSource}
            pagination={pagination}
            getGraphMissingImagesList={getGraphMissingImagesList}
            isMissingConnectorOrTerminalImg={isMissingConnectorOrTerminalImg}
            isMissingVehicleImg={isMissingVehicleImg}
            onChangeIsMissingConnectorOrTerminalImg={
              setIsMissingConnectorOrTerminalImg
            }
            onChangeIsMissingVehicleImg={setIsMissingVehicleImg}
          />
        </Flex>
      </Row>
    </div>
  )
}

export default GraphMissingImagesList
